import { useCallback, useState } from 'react';

import { SectionNav } from '@/components/SectionNav';
import { useHashScrollSpy } from '@/hooks/useHashScrollSpy';

import { InvestmentPolicyOverview } from './InvestmentPolicyOverview';
import { PolicyAssetLocation } from './PolicyAssetLocation';
import { PolicyCashEq } from './PolicyCashEq';
import { PolicyExclusions } from './PolicyExclusions';
import { PolicyPersonalizedIndexing } from './PolicyPersonalizedIndexing';
import { PolicySectionBoundary } from './PolicySectionBoundary';
import { PolicyTaxes } from './PolicyTaxes';
import { PolicyWithdrawals } from './PolicyWithdrawals';

const detailSections = ['Growth', 'Taxes', 'Withdrawal', 'Exclusions'] as const;
const policySections = ['Overview', ...detailSections] as const;

const PolicyGrowth = () => (
    <>
        <h2 id="policy-growth-title" className="text-cocoa-900 mb-4 text-2xl font-medium">
            Growth
        </h2>
        <div className="space-y-4.5">
            <PolicySectionBoundary name="Asset Location">
                <PolicyAssetLocation />
            </PolicySectionBoundary>
            <PolicySectionBoundary name="Personalized Indexing">
                <PolicyPersonalizedIndexing />
            </PolicySectionBoundary>
            <PolicySectionBoundary name="Cash EQ">
                <PolicyCashEq />
            </PolicySectionBoundary>
        </div>
    </>
);

const sectionContent = {
    Growth: PolicyGrowth,
    Taxes: PolicyTaxes,
    Withdrawal: PolicyWithdrawals,
    Exclusions: PolicyExclusions,
};

export const BoostedInvestmentPolicy = () => {
    const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
    const contentRef = useCallback((element: HTMLDivElement | null) => {
        // AccountPage renders TabsContent directly, outside RoutingTabs' panel context.
        setScrollRoot(element?.closest<HTMLElement>('[role="tabpanel"]') ?? null);
    }, []);
    const { activeSection, sectionRef, endRef, scrollToSection } = useHashScrollSpy(policySections, scrollRoot);

    return (
        <div ref={contentRef} className="flex flex-col gap-6 pt-6 lg:flex-row lg:gap-12">
            <SectionNav
                sections={policySections}
                activeSection={activeSection}
                onSelect={scrollToSection}
                showIndicators
                ariaLabel="Investment policy sections"
                className="[&_button]:font-medium"
            />
            <div className="min-w-0 flex-1 lg:max-w-[1240px]">
                <div className="space-y-16">
                    <section ref={sectionRef('Overview')} aria-label="Overview" className="scroll-mt-4">
                        <PolicySectionBoundary name="Overview">
                            <InvestmentPolicyOverview onTaxBudgetSelect={() => scrollToSection('Taxes')} />
                        </PolicySectionBoundary>
                    </section>
                    {detailSections.map((section) => {
                        const Content = sectionContent[section];
                        return (
                            <section
                                key={section}
                                ref={sectionRef(section)}
                                aria-labelledby={`policy-${section.toLowerCase()}-title`}
                                className="scroll-mt-4"
                            >
                                <PolicySectionBoundary name={section}>
                                    <Content />
                                </PolicySectionBoundary>
                            </section>
                        );
                    })}
                </div>
                <div ref={endRef} aria-hidden="true" className="mt-12 h-px" />
            </div>
        </div>
    );
};
